// Data Store abstraction layer
// This allows easy switching between localStorage, Firestore, or API backends

export interface DataStore<T> {
  getAll(): Promise<T[]>;
  getById(id: string): Promise<T | null>;
  save(item: T): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

// Helper to create localStorage-backed stores
export function createLocalStore<T extends { id: string }>(key: string): DataStore<T> {
  return {
    async getAll(): Promise<T[]> {
      try {
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : [];
      } catch {
        return [];
      }
    },

    async getById(id: string): Promise<T | null> {
      const all = await this.getAll();
      return all.find(item => item.id === id) || null;
    },

    async save(item: T): Promise<void> {
      const all = await this.getAll();
      const index = all.findIndex(i => i.id === item.id);
      
      const now = new Date().toISOString();
      const itemWithTimestamps = {
        ...item,
        updatedAt: now,
        createdAt: index === -1 ? now : (all[index] as any).createdAt || now,
      };
      
      if (index === -1) {
        all.push(itemWithTimestamps);
      } else {
        all[index] = itemWithTimestamps;
      }
      
      localStorage.setItem(key, JSON.stringify(all));
    },

    async delete(id: string): Promise<void> {
      const all = await this.getAll();
      const filtered = all.filter(item => item.id !== id);
      localStorage.setItem(key, JSON.stringify(filtered));
    },

    async clear(): Promise<void> {
      localStorage.removeItem(key);
    },
  };
}

// Future: Firestore-backed store
// export function createFirestoreStore<T extends { id: string }>(collectionName: string): DataStore<T> {
//   return {
//     async getAll() { /* Firestore query */ },
//     async getById(id) { /* Firestore doc get */ },
//     async save(item) { /* Firestore set */ },
//     async delete(id) { /* Firestore delete */ },
//     async clear() { /* Firestore batch delete */ },
//   };
// }

// Generate unique IDs
export function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
